// Master Template Library Database for STUDLYF Templates Marketplace
// Supports dynamic image detection, rich category indexing, and full multi-slide presentation capabilities.

export const ALL_CATEGORIES = [
  "All Templates",
  "Startup",
  "Business",
  "Marketing",
  "Finance",
  "Sales",
  "Education",
  "Technology",
  "Product",
  "Healthcare",
  "Sustainability",
  "HR",
  "Portfolio"
];

// Custom slide templates generator providing unique slide structures for every category
const generateUniqueSlides = (tmplId, tmplTitle, tmplCategory, defaultImage, slide2Img, slide3Img, slide4Img) => {
  if (tmplId.includes("pitch-deck") || tmplId.includes("seed") || tmplId.includes("series-a") || tmplId.includes("investor")) {
    return [
      {
        number: 1,
        title: tmplTitle,
        subtitle: `${tmplCategory} | Cover Slide`,
        layoutType: "hero",
        bullets: "STUDLYF Investor-Grade Presentation Deck. Designed to present vision, traction, and raise venture capital.",
        image: defaultImage
      },
      {
        number: 2,
        title: "The Problem",
        subtitle: "Market Pain Points",
        layoutType: "standard-light",
        bullets: "• Outdated, manual workflows causing high operating friction.\n• Severe inefficiencies costing industry leaders millions annually.\n• Fragmented tools fail to address modern customer expectations.",
        image: slide2Img
      },
      {
        number: 3,
        title: "The Solution",
        subtitle: "Core Value Proposition",
        layoutType: "standard-dark",
        bullets: "• Next-generation AI platform replacing legacy manual processes.\n• 10x faster execution speed with automated workflow pipelines.\n• Seamless integration into enterprise tech stacks.",
        image: slide3Img
      },
      {
        number: 4,
        title: "Market Opportunity & TAM",
        subtitle: "Target Market Size",
        layoutType: "metrics",
        bullets: "Massive addressable market driven by rapid digital transformation and secular industry tailwinds.",
        metrics: [
          { value: "$18.4B", label: "Total Addressable Market (TAM)" },
          { value: "$3.2B", label: "Serviceable Addressable Market (SAM)" },
          { value: "$450M", label: "Target 3-Year Market Capture (SOM)" }
        ],
        image: defaultImage
      },
      {
        number: 5,
        title: "Business Model & Monetization",
        subtitle: "Revenue Streams",
        layoutType: "two-column",
        bullets: "Diversified, recurring SaaS revenue streams with high expansion potential.",
        columns: [
          { title: "Subscription Tiers", content: "• Starter: $49/mo\n• Growth Pro: $199/mo\n• Enterprise: Custom ARR contracts" },
          { title: "Expansion Drivers", content: "• Usage-based API consumption\n• Seat license expansion\n• Premium marketplace integrations" }
        ],
        image: slide2Img
      },
      {
        number: 6,
        title: "Traction & Milestones",
        subtitle: "Growth Trajectory",
        layoutType: "timeline",
        bullets: "Rapid commercial velocity driven by product-led growth and expansion.",
        timeline: [
          { label: "Q1 Launch", description: "MVP deployed with 1,200 active beta users." },
          { label: "Q2 Monetization", description: "Achieved $25K MRR with 14% MoM organic growth." },
          { label: "Q3 Scale", description: "Expanding sales pipeline to reach $1M ARR benchmark." }
        ],
        image: slide3Img
      },
      {
        number: 7,
        title: "The Ask & Funding Use",
        subtitle: "Investment Required",
        layoutType: "closing",
        bullets: "Raising Seed/Series A capital to scale engineering, expand GTM team, and accelerate customer acquisition.",
        image: slide4Img
      }
    ];
  }

  if (tmplId.includes("business-plan") || tmplId.includes("proposal") || tmplId.includes("company")) {
    return [
      {
        number: 1,
        title: tmplTitle,
        subtitle: `${tmplCategory} | Master Blueprint`,
        layoutType: "hero",
        bullets: "Comprehensive corporate outline detailing strategic goals, operational structure, and financial milestones.",
        image: defaultImage
      },
      {
        number: 2,
        title: "Executive Summary",
        subtitle: "Company Vision & Mission",
        layoutType: "standard-light",
        bullets: "• Vision: Empowering organizations with unified digital intelligence.\n• Mission: Building intuitive, high-impact enterprise solutions.\n• Core Strategy: Accelerating market expansion through product excellence.",
        image: slide2Img
      },
      {
        number: 3,
        title: "Industry & Market Analysis",
        subtitle: "Market Ecosystem",
        layoutType: "two-column",
        bullets: "Comprehensive evaluation of market growth dynamics and key demand drivers.",
        columns: [
          { title: "Market Growth", content: "• 22.4% CAGR expected over 5 years.\n• Increasing demand for cloud-native workflows." },
          { title: "Competitive Edge", content: "• Proprietary IP and patent-pending architecture.\n• 40% lower TCO compared to legacy incumbents." }
        ],
        image: slide3Img
      },
      {
        number: 4,
        title: "Operations & Execution Plan",
        subtitle: "Implementation Roadmap",
        layoutType: "timeline",
        bullets: "Key operational phases for scaling team, infrastructure, and compliance.",
        timeline: [
          { label: "Phase 1: Infrastructure", description: "Finalize core security certifications and enterprise cloud stack." },
          { label: "Phase 2: Market Penetration", description: "Deploy direct sales teams in key strategic metropolitan regions." },
          { label: "Phase 3: Operational Scale", description: "Automate customer onboarding and scale support operations." }
        ],
        image: defaultImage
      },
      {
        number: 5,
        title: "Financial Projections & Milestones",
        subtitle: "Key Performance Targets",
        layoutType: "metrics",
        bullets: "Projected financial milestones over the next 3 fiscal years.",
        metrics: [
          { value: "$2.1M", label: "Year 1 Revenue Target" },
          { value: "$6.8M", label: "Year 2 Revenue Target" },
          { value: "$18.5M", label: "Year 3 Revenue Target" }
        ],
        image: slide4Img
      }
    ];
  }

  if (tmplId.includes("marketing") || tmplId.includes("social") || tmplId.includes("campaign") || tmplId.includes("brand")) {
    return [
      {
        number: 1,
        title: tmplTitle,
        subtitle: `${tmplCategory} | Marketing Strategy Blueprint`,
        layoutType: "hero",
        bullets: "Data-driven marketing campaign blueprint engineered to maximize customer acquisition and brand equity.",
        image: defaultImage
      },
      {
        number: 2,
        title: "Target Audience & ICP",
        subtitle: "Customer Personas",
        layoutType: "standard-light",
        bullets: "• Primary ICP: High-growth startup founders, product leads, and marketing managers.\n• Key Demographics: Tech-savvy professionals aged 25–45 in tier-1 innovation hubs.\n• Primary Pain Points: Fragmented marketing stacks, high CAC, and low lead retention.",
        image: slide2Img
      },
      {
        number: 3,
        title: "Marketing Channels & Mix",
        subtitle: "Omnichannel Distribution",
        layoutType: "two-column",
        bullets: "Multi-touch distribution strategy across paid, organic, and referral channels.",
        columns: [
          { title: "Organic & Inbound", content: "• SEO & Thought Leadership Content\n• Social Media (LinkedIn, Twitter, YouTube)\n• Developer & Founder Community Hubs" },
          { title: "Paid & Outbound", content: "• High-intent Search Engine Ads\n• Retargeting & Social Ad Campaigns\n• Performance Partner Affiliates" }
        ],
        image: slide3Img
      },
      {
        number: 4,
        title: "Campaign Timeline & Execution",
        subtitle: "Launch Phases",
        layoutType: "timeline",
        bullets: "Structured campaign execution roadmap from pre-launch buzz to viral momentum.",
        timeline: [
          { label: "Pre-Launch Teasers", description: "Build VIP waitlist, publish teaser videos, and seed influencer reviews." },
          { label: "Launch Day Blitz", description: "Coordinated Product Hunt release, press distribution, and live demo webinars." },
          { label: "Post-Launch Retargeting", description: "Nurture trial signups with email drips and retargeting ads." }
        ],
        image: defaultImage
      },
      {
        number: 5,
        title: "Marketing KPIs & Budget Allocations",
        subtitle: "Performance Targets",
        layoutType: "metrics",
        bullets: "Target metrics and ROI benchmarks for campaign evaluation.",
        metrics: [
          { value: "4.2x", label: "Target ROAS" },
          { value: "$32", label: "Target CAC" },
          { value: "250K+", label: "Monthly Qualified Impressions" }
        ],
        image: slide4Img
      }
    ];
  }

  if (tmplId.includes("financial") || tmplId.includes("budget") || tmplId.includes("valuation") || tmplId.includes("revenue")) {
    return [
      {
        number: 1,
        title: tmplTitle,
        subtitle: `${tmplCategory} | Financial Model Framework`,
        layoutType: "hero",
        bullets: "Quantitative financial forecast model covering revenue streams, operating expenses, and cash runway.",
        image: defaultImage
      },
      {
        number: 2,
        title: "Revenue Model & Forecast",
        subtitle: "Growth Assumptions",
        layoutType: "metrics",
        bullets: "Multi-year revenue trajectory based on customer acquisition velocity and expansion MRR.",
        metrics: [
          { value: "$120K", label: "Monthly Recurring Revenue (MRR)" },
          { value: "88%", label: "Gross Margin Percentage" },
          { value: "24 Mo", label: "Current Cash Runway" }
        ],
        image: slide2Img
      },
      {
        number: 3,
        title: "Operating Expense Breakdown",
        subtitle: "OPEX Structure",
        layoutType: "two-column",
        bullets: "Disciplined capital allocation across core functional departments.",
        columns: [
          { title: "R&D & Engineering (45%)", content: "• Core software engineering salaries\n• Cloud infrastructure & AI API costs\n• Security auditing & compliance" },
          { title: "Sales & Marketing (35%)", content: "• Paid acquisition campaigns\n• GTM team commissions & salaries\n• Event sponsorships & PR" }
        ],
        image: slide3Img
      },
      {
        number: 4,
        title: "Cash Flow & Break-Even Analysis",
        subtitle: "Path to Profitability",
        layoutType: "timeline",
        bullets: "Key milestones leading toward net positive operating cash flow.",
        timeline: [
          { label: "Q2: Cash Burn Peak", description: "Capital deployed heavily into GTM headcount and product features." },
          { label: "Q4: Operational Break-Even", description: "Monthly recurring revenue offsets total operating expenditures." },
          { label: "Year 2: Net Profitability", description: "Sustained positive EBITDA margin with internal reinvestment." }
        ],
        image: slide4Img
      }
    ];
  }

  // Default universal slide structure for specialized niche templates
  return [
    {
      number: 1,
      title: tmplTitle,
      subtitle: `${tmplCategory} | Professional Blueprint`,
      layoutType: "hero",
      bullets: `STUDLYF master template designed for ${tmplTitle}. Customize every text block, chart, and layout element.`,
      image: defaultImage
    },
    {
      number: 2,
      title: "1. Core Objectives & Overview",
      subtitle: "Strategic Focus",
      layoutType: "standard-light",
      bullets: "• Clear operational objectives and target deliverables.\n• Key stakeholder roles and expected performance outcomes.\n• Strategic alignment with overall business growth metrics.",
      image: slide2Img
    },
    {
      number: 3,
      title: "2. Key Components & Layout",
      subtitle: "Structured Execution",
      layoutType: "two-column",
      bullets: "Modular execution framework designed for immediate adoption.",
      columns: [
        { title: "Primary Deliverables", content: "• Detailed documentation frameworks.\n• Interactive tracking & metrics inputs.\n• Standardized review procedures." },
        { title: "Expected Impact", content: "• Accelerated team execution speed.\n• Reduced operational complexity.\n• High-quality standardized reporting." }
      ],
      image: slide3Img
    },
    {
      number: 4,
      title: "3. Implementation Roadmap",
      subtitle: "Action Plan",
      layoutType: "timeline",
      bullets: "Step-by-step rollout timeline for seamless deployment.",
      timeline: [
        { label: "Phase 1: Initial Setup", description: "Review template parameters and input custom company data." },
        { label: "Phase 2: Team Adoption", description: "Share documentation with internal stakeholders and review feedback." },
        { label: "Phase 3: Execution & Audit", description: "Monitor performance benchmarks and refine workflow iteration." }
      ],
      image: slide4Img
    }
  ];
};

// Master Template Collection covering 71 unique templates across 12 distinct categories with dedicated images
export const TEMPLATES_DATA = [
  // ─── 🚀 STARTUP CATEGORY (8 TEMPLATES) ───
  {
    id: "startup-pitch-deck",
    title: "Startup Pitch Deck Master",
    description: "Sequoia & YC-inspired pitch deck framework to present vision, metrics, and close venture capital rounds.",
    category: "Startup",
    time: "2 hours",
    downloads: 48200,
    likes: 3420,
    views: 95400,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["SaaS", "AI", "FinTech"],
    stage: ["Pre-Seed", "Seed", "Series A"],
    creator: "STUDLYF Venture Team",
    tags: ["Pitch Deck", "Fundraising", "VC", "Startup"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide1.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide1.png",
      "/templates/previews/pitch-deck-slide2.png",
      "/templates/previews/pitch-deck-slide3.png",
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("startup-pitch-deck", "Startup Pitch Deck Master", "Startup", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide3.png", "/templates/previews/pitch-deck-slide4.png")
  },
  {
    id: "seed-pitch-deck",
    title: "Seed Pitch Deck",
    description: "Tailored presentation deck focused on product-market fit signals, early traction, and seed funding ask.",
    category: "Startup",
    time: "1.5 hours",
    downloads: 29400,
    likes: 2180,
    views: 61200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["SaaS", "Mobile", "Marketplace"],
    stage: ["Pre-Seed", "Seed"],
    creator: "STUDLYF Venture Team",
    tags: ["Seed Pitch", "Fundraising", "Early Stage"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide2.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide2.png",
      "/templates/previews/pitch-deck-slide1.png",
      "/templates/previews/pitch-deck-slide3.png",
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("seed-pitch-deck", "Seed Pitch Deck", "Startup", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/pitch-deck-slide3.png", "/templates/previews/pitch-deck-slide4.png")
  },
  {
    id: "series-a-deck",
    title: "Series A Expansion Deck",
    description: "Data-heavy institutional presentation deck highlighting ARR growth curves, unit economics, and scaling strategy.",
    category: "Startup",
    time: "2.5 hours",
    downloads: 18900,
    likes: 1540,
    views: 42100,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["SaaS", "AI", "FinTech", "DeepTech"],
    stage: ["Series A", "Series B"],
    creator: "Institutional VC Advisory",
    tags: ["Series A", "Growth", "Scale", "ARR Metrics"],
    isPopular: false,
    isNew: false,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide3.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide3.png",
      "/templates/previews/pitch-deck-slide1.png",
      "/templates/previews/pitch-deck-slide2.png",
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("series-a-deck", "Series A Expansion Deck", "Startup", "/templates/previews/pitch-deck-slide3.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide4.png")
  },
  {
    id: "business-plan-blueprint",
    title: "Comprehensive Business Plan",
    description: "Operational master blueprint detailing executive summary, strategic objectives, and 3-year financial projections.",
    category: "Startup",
    time: "3 hours",
    downloads: 38100,
    likes: 2890,
    views: 78400,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["General", "E-commerce", "SaaS"],
    stage: ["Idea", "MVP", "Pre-Seed"],
    creator: "STUDLYF Strategy Lab",
    tags: ["Business Plan", "Operations", "Strategy"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png",
      "/templates/previews/user-template-finance.png",
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("business-plan-blueprint", "Comprehensive Business Plan", "Startup", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-finance.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "lean-canvas-template",
    title: "Lean Canvas Blueprint",
    description: "1-page agile business model layout breaking down problem, solution, key metrics, and unfair advantage.",
    category: "Startup",
    time: "45 mins",
    downloads: 41200,
    likes: 3100,
    views: 89000,
    rating: 4.9,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["Idea", "MVP"],
    creator: "Ash Maurya Framework",
    tags: ["Lean Canvas", "Agile", "Business Model"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide1.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide1.png",
      "/templates/previews/pitch-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("lean-canvas-template", "Lean Canvas Blueprint", "Startup", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide3.png", "/templates/previews/pitch-deck-slide4.png")
  },
  {
    id: "one-pager-exec-summary",
    title: "One Pager Executive Summary",
    description: "Ultra-concise single-page executive overview ideal for investor intros and advisor outreach.",
    category: "Startup",
    time: "30 mins",
    downloads: 25600,
    likes: 1890,
    views: 51200,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["Idea", "Pre-Seed", "Seed"],
    creator: "STUDLYF Editorial",
    tags: ["One Pager", "Teaser", "Executive Summary"],
    isPopular: false,
    isNew: true,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide3.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("one-pager-exec-summary", "One Pager Executive Summary", "Startup", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "product-vision-deck",
    title: "Product Vision & Strategy",
    description: "Strategic deck aligning founders, product managers, and engineering leads on long-term product vision.",
    category: "Startup",
    time: "1 hour",
    downloads: 16400,
    likes: 1320,
    views: 34100,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["SaaS", "AI", "Consumer"],
    stage: ["MVP", "Seed"],
    creator: "Product Strategy Collective",
    tags: ["Product Vision", "Roadmap", "Strategy"],
    isPopular: false,
    isNew: false,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide4.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide4.png",
      "/templates/previews/pitch-deck-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-vision-deck", "Product Vision & Strategy", "Startup", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide3.png")
  },
  {
    id: "startup-roadmap-deck",
    title: "Startup Execution Roadmap",
    description: "Quarterly milestone tracking deck detailing product releases, GTM objectives, and hiring targets.",
    category: "Startup",
    time: "1 hour",
    downloads: 21300,
    likes: 1670,
    views: 45200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["All Industries"],
    stage: ["MVP", "Seed", "Series A"],
    creator: "STUDLYF Growth Team",
    tags: ["Roadmap", "Milestones", "Execution"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("startup-roadmap-deck", "Startup Execution Roadmap", "Startup", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },

  // ─── 💼 BUSINESS CATEGORY (8 TEMPLATES) ───
  {
    id: "company-profile-deck",
    title: "Company Profile Presentation",
    description: "Sleek corporate profile deck for presenting company history, team, services, and client portfolio.",
    category: "Business",
    time: "1.5 hours",
    downloads: 34200,
    likes: 2450,
    views: 68900,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Corporate", "Services", "Agency"],
    stage: ["All Stages"],
    creator: "STUDLYF Design Studio",
    tags: ["Company Profile", "Corporate", "Services"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png",
      "/templates/previews/studlyf-master-deck.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("company-profile-deck", "Company Profile Presentation", "Business", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png")
  },
  {
    id: "company-overview-deck",
    title: "Corporate Company Overview",
    description: "Executive-level overview deck designed for client pitches, vendor meetings, and public presentations.",
    category: "Business",
    time: "1 hour",
    downloads: 22100,
    likes: 1720,
    views: 48900,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Corporate", "Enterprise"],
    stage: ["All Stages"],
    creator: "STUDLYF Design Studio",
    tags: ["Overview", "Corporate", "Presentation"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("company-overview-deck", "Corporate Company Overview", "Business", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck-slide4.png")
  },
  {
    id: "business-proposal-blueprint",
    title: "Enterprise Business Proposal",
    description: "High-converting proposal structure detailing scope of work, timelines, pricing options, and SLA terms.",
    category: "Business",
    time: "2 hours",
    downloads: 29800,
    likes: 2190,
    views: 59800,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["B2B Services", "Consulting", "Agency"],
    stage: ["All Stages"],
    creator: "Enterprise Sales Network",
    tags: ["Proposal", "B2B", "Sales", "Contract"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-finance.png",
    galleryImages: [
      "/templates/previews/user-template-finance.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("business-proposal-blueprint", "Enterprise Business Proposal", "Business", "/templates/previews/user-template-finance.png", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "partnership-proposal-deck",
    title: "Strategic Partnership Proposal",
    description: "Collaborative partnership deck outlining co-marketing, joint integration, and shared revenue models.",
    category: "Business",
    time: "1.5 hours",
    downloads: 17800,
    likes: 1420,
    views: 38900,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["SaaS", "FinTech", "Corporate"],
    stage: ["Seed", "Series A", "Growth"],
    creator: "BD Alliances Lead",
    tags: ["Partnership", "Co-Marketing", "Alliance"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("partnership-proposal-deck", "Strategic Partnership Proposal", "Business", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "corporate-deck",
    title: "Executive Corporate Deck",
    description: "Clean, professional corporate deck suited for board reviews, shareholder updates, and strategic announcements.",
    category: "Business",
    time: "1.5 hours",
    downloads: 19400,
    likes: 1510,
    views: 41200,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Corporate"],
    stage: ["Growth", "Enterprise"],
    creator: "STUDLYF Corporate",
    tags: ["Corporate", "Board Meeting", "Governance"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide2.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("corporate-deck", "Executive Corporate Deck", "Business", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "annual-report-template",
    title: "Annual Performance Report",
    description: "Comprehensive annual report presentation summarizing revenue milestones, operational growth, and ESG goals.",
    category: "Business",
    time: "2 hours",
    downloads: 15200,
    likes: 1190,
    views: 32400,
    rating: 4.8,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Corporate", "Finance"],
    stage: ["Growth", "Enterprise"],
    creator: "Corporate Reporting Group",
    tags: ["Annual Report", "Financials", "Performance"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("annual-report-template", "Annual Performance Report", "Business", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-finance.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "quarterly-report-deck",
    title: "Quarterly Business Review (QBR)",
    description: "Standardized QBR presentation for evaluating quarterly OKRs, team velocity, and upcoming goals.",
    category: "Business",
    time: "1 hour",
    downloads: 26400,
    likes: 1980,
    views: 54100,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF Operations Lab",
    tags: ["QBR", "Quarterly Report", "OKRs"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide2.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("quarterly-report-deck", "Quarterly Business Review (QBR)", "Business", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "meeting-presentation-deck",
    title: "All-Hands Meeting Deck",
    description: "Engaging internal company presentation template for monthly all-hands meetings and team updates.",
    category: "Business",
    time: "45 mins",
    downloads: 28900,
    likes: 2120,
    views: 58900,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF HR & Ops",
    tags: ["All Hands", "Meeting", "Internal"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("meeting-presentation-deck", "All-Hands Meeting Deck", "Business", "/templates/previews/user-template-social.png", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png")
  },

  // ─── 📊 MARKETING CATEGORY (8 TEMPLATES) ───
  {
    id: "marketing-strategy-blueprint",
    title: "Omnichannel Marketing Strategy",
    description: "Data-driven marketing strategy blueprint covering target ICPs, channel mix, budget split, and KPI dashboards.",
    category: "Marketing",
    time: "2 hours",
    downloads: 36800,
    likes: 2790,
    views: 74200,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["SaaS", "E-commerce", "D2C"],
    stage: ["MVP", "Seed", "Growth"],
    creator: "STUDLYF Growth Team",
    tags: ["Marketing Strategy", "GTM", "Acquisition"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png",
      "/templates/previews/marketing-plan-slide1.png",
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("marketing-strategy-blueprint", "Omnichannel Marketing Strategy", "Marketing", "/templates/previews/user-template-marketing.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/marketing-plan-slide3.png")
  },
  {
    id: "go-to-market-plan",
    title: "Go-To-Market (GTM) Launch Plan",
    description: "Actionable GTM framework mapping positioning, distribution partnerships, beta pricing, and launch timelines.",
    category: "Marketing",
    time: "2 hours",
    downloads: 31200,
    likes: 2340,
    views: 64100,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["SaaS", "AI", "FinTech"],
    stage: ["MVP", "Pre-Seed", "Seed"],
    creator: "GTM Advisory Collective",
    tags: ["GTM", "Launch", "Product Launch"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png",
      "/templates/previews/marketing-plan-slide2.png",
      "/templates/previews/marketing-plan-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("go-to-market-plan", "Go-To-Market (GTM) Launch Plan", "Marketing", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "brand-guidelines-deck",
    title: "Brand Identity & Style Guidelines",
    description: "Visual identity design system detailing color palettes, typography scales, logo usage rules, and brand voice.",
    category: "Marketing",
    time: "1.5 hours",
    downloads: 24500,
    likes: 1890,
    views: 52100,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Branding", "Design", "Agency"],
    stage: ["All Stages"],
    creator: "STUDLYF Brand Lab",
    tags: ["Brand Guidelines", "Design System", "Branding"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide2.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("brand-guidelines-deck", "Brand Identity & Style Guidelines", "Marketing", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "campaign-proposal-deck",
    title: "Marketing Campaign Proposal",
    description: "High-impact campaign pitch deck designed for marketing agencies to present creative concepts and media plans.",
    category: "Marketing",
    time: "1.5 hours",
    downloads: 18900,
    likes: 1420,
    views: 39800,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["Agency", "Advertising"],
    stage: ["All Stages"],
    creator: "Agency Growth Network",
    tags: ["Campaign Proposal", "Advertising", "Pitch"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/marketing-plan-slide3.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("campaign-proposal-deck", "Marketing Campaign Proposal", "Marketing", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "digital-marketing-plan",
    title: "Digital Performance Marketing Plan",
    description: "Performance marketing deck detailing paid search, Meta ads, SEO content strategy, and conversion rate optimization.",
    category: "Marketing",
    time: "1.5 hours",
    downloads: 27400,
    likes: 2050,
    views: 58200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["E-commerce", "SaaS", "D2C"],
    stage: ["Seed", "Growth"],
    creator: "STUDLYF Growth Team",
    tags: ["Digital Marketing", "SEO", "PPC", "Growth"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("digital-marketing-plan", "Digital Performance Marketing Plan", "Marketing", "/templates/previews/user-template-social.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "social-media-strategy-plan",
    title: "Social Media Strategy Plan",
    description: "Comprehensive content calendar, channel strategy, community growth roadmap, and influencer marketing framework.",
    category: "Marketing",
    time: "1 hour",
    downloads: 33100,
    likes: 2510,
    views: 69400,
    rating: 4.9,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Consumer", "Media", "D2C"],
    stage: ["All Stages"],
    creator: "Social Growth Lab",
    tags: ["Social Media", "Content", "Instagram", "LinkedIn"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/linkedin-thought-leadership.png",
    galleryImages: [
      "/templates/previews/linkedin-thought-leadership.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("social-media-strategy-plan", "Social Media Strategy Plan", "Marketing", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/user-template-social.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "product-launch-campaign",
    title: "Product Launch Marketing Campaign",
    description: "Product Hunt & press release launch presentation detailing viral loops, VIP waitlists, and launch week blitz.",
    category: "Marketing",
    time: "1.5 hours",
    downloads: 21400,
    likes: 1680,
    views: 46100,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["SaaS", "AI", "Mobile"],
    stage: ["MVP", "Seed"],
    creator: "Launchpad Collective",
    tags: ["Product Launch", "Product Hunt", "PR"],
    isPopular: false,
    isNew: true,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide4.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-launch-campaign", "Product Launch Marketing Campaign", "Marketing", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "email-marketing-strategy",
    title: "Email Marketing Drip Strategy",
    description: "Automated email lifecycle framework covering welcome series, lead nurturing, trial conversion, and churn prevention.",
    category: "Marketing",
    time: "1 hour",
    downloads: 19800,
    likes: 1490,
    views: 42100,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["SaaS", "E-commerce"],
    stage: ["All Stages"],
    creator: "STUDLYF Growth Team",
    tags: ["Email Marketing", "Automation", "Retention"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("email-marketing-strategy", "Email Marketing Drip Strategy", "Marketing", "/templates/previews/user-template-marketing.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/user-template-marketing.png")
  },

  // ─── 💰 FINANCE CATEGORY (8 TEMPLATES) ───
  {
    id: "saas-financial-model",
    title: "SaaS Financial Model Master",
    description: "3-year financial model covering ARR/MRR forecasts, CAC payback metrics, burn rate, and gross margin analysis.",
    category: "Finance",
    time: "2 hours",
    downloads: 28900,
    likes: 2140,
    views: 59400,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["SaaS", "AI", "FinTech"],
    stage: ["Seed", "Series A"],
    creator: "CFO Advisory Network",
    tags: ["Financial Model", "ARR", "Metrics", "SaaS"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-finance.png",
    galleryImages: [
      "/templates/previews/user-template-finance.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("saas-financial-model", "SaaS Financial Model Master", "Finance", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "revenue-forecast-model",
    title: "Revenue Forecast & Growth Projection",
    description: "Multi-scenario revenue forecast model detailing pipeline conversions, expansion ARR, and churn sensitivity.",
    category: "Finance",
    time: "1.5 hours",
    downloads: 23100,
    likes: 1780,
    views: 48900,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF Finance Lab",
    tags: ["Revenue", "Forecast", "Financials"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide3.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("revenue-forecast-model", "Revenue Forecast & Growth Projection", "Finance", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-finance.png", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "budget-planning-framework",
    title: "Annual Budget Planning Framework",
    description: "Departmental budget allocation template covering payroll, software licenses, marketing, and emergency reserves.",
    category: "Finance",
    time: "1.5 hours",
    downloads: 20400,
    likes: 1560,
    views: 43200,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Corporate", "SaaS"],
    stage: ["Seed", "Series A", "Growth"],
    creator: "STUDLYF Finance Lab",
    tags: ["Budget", "OPEX", "Finance"],
    isPopular: false,
    isNew: true,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("budget-planning-framework", "Annual Budget Planning Framework", "Finance", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "investment-proposal-deck",
    title: "Strategic Investment Proposal",
    description: "Investor proposal deck detailing capital expenditure, projected IRR, dividend distribution, and exit options.",
    category: "Finance",
    time: "2 hours",
    downloads: 17900,
    likes: 1390,
    views: 38200,
    rating: 4.8,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Real Estate", "Private Equity", "Startups"],
    stage: ["Series A", "Growth"],
    creator: "PE Investment Group",
    tags: ["Investment", "IRR", "Equity", "Proposal"],
    isPopular: false,
    isNew: false,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-finance.png",
    galleryImages: [
      "/templates/previews/user-template-finance.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("investment-proposal-deck", "Strategic Investment Proposal", "Finance", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "valuation-report-deck",
    title: "Startup Valuation Report",
    description: "DCF and comparable transaction valuation deck outlining pre-money valuation benchmarks and cap table impact.",
    category: "Finance",
    time: "2 hours",
    downloads: 14800,
    likes: 1120,
    views: 31200,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Startups", "FinTech"],
    stage: ["Seed", "Series A"],
    creator: "Valuation & M&A Advisors",
    tags: ["Valuation", "Cap Table", "DCF"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide3.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("valuation-report-deck", "Startup Valuation Report", "Finance", "/templates/previews/pitch-deck-slide3.png", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "profit-and-loss-statement",
    title: "Profit & Loss (P&L) Statement Deck",
    description: "Clean executive presentation deck summarizing quarterly gross revenue, COGS, operating profit, and net income.",
    category: "Finance",
    time: "1 hour",
    downloads: 25200,
    likes: 1890,
    views: 52400,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF Finance Lab",
    tags: ["P&L", "Profit", "Income Statement"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide2.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("profit-and-loss-statement", "Profit & Loss (P&L) Statement Deck", "Finance", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "break-even-analysis-model",
    title: "Break-even Analysis Model",
    description: "Financial model determining unit volume and revenue thresholds required to achieve net zero operating burn.",
    category: "Finance",
    time: "1 hour",
    downloads: 19200,
    likes: 1430,
    views: 41200,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["All Industries"],
    stage: ["Idea", "MVP", "Seed"],
    creator: "STUDLYF Finance Lab",
    tags: ["Break-even", "Unit Economics", "Margin"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide2.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("break-even-analysis-model", "Break-even Analysis Model", "Finance", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "financial-dashboard-report",
    title: "Executive Financial Dashboard",
    description: "High-level CFO dashboard template visualizing cash burn, runway, gross margin, and monthly expenses.",
    category: "Finance",
    time: "1 hour",
    downloads: 22800,
    likes: 1740,
    views: 48900,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["Corporate", "SaaS"],
    stage: ["All Stages"],
    creator: "CFO Dashboard Hub",
    tags: ["Dashboard", "CFO", "Metrics"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-finance.png",
    galleryImages: [
      "/templates/previews/user-template-finance.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("financial-dashboard-report", "Executive Financial Dashboard", "Finance", "/templates/previews/user-template-finance.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png")
  },

  // ─── 📈 SALES CATEGORY (6 TEMPLATES) ───
  {
    id: "enterprise-sales-deck",
    title: "Enterprise B2B Sales Deck",
    description: "High-converting B2B sales presentation deck addressing pain points, ROI calculator, and security compliance.",
    category: "Sales",
    time: "1.5 hours",
    downloads: 31400,
    likes: 2390,
    views: 64200,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["SaaS", "Enterprise", "B2B"],
    stage: ["Seed", "Series A", "Growth"],
    creator: "STUDLYF Sales Academy",
    tags: ["Sales Deck", "B2B", "Enterprise"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide2.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("enterprise-sales-deck", "Enterprise B2B Sales Deck", "Sales", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "sales-strategy-blueprint",
    title: "Outbound Sales Strategy Blueprint",
    description: "Structured outbound sales process framework mapping SDR prospecting, email sequences, and demo closes.",
    category: "Sales",
    time: "1.5 hours",
    downloads: 21900,
    likes: 1640,
    views: 46200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["SaaS", "Services"],
    stage: ["All Stages"],
    creator: "Sales Growth Advisory",
    tags: ["Sales Strategy", "Outbound", "SDR"],
    isPopular: false,
    isNew: true,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide2.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("sales-strategy-blueprint", "Outbound Sales Strategy Blueprint", "Sales", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "pricing-strategy-deck",
    title: "SaaS Pricing & Packaging Deck",
    description: "Strategic pricing presentation analyzing tier structures, feature bundling, and annual contract discounts.",
    category: "Sales",
    time: "1 hour",
    downloads: 18400,
    likes: 1390,
    views: 39100,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["SaaS", "E-commerce"],
    stage: ["MVP", "Seed"],
    creator: "STUDLYF Strategy Lab",
    tags: ["Pricing", "Packaging", "Monetization"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("pricing-strategy-deck", "SaaS Pricing & Packaging Deck", "Sales", "/templates/previews/user-template-marketing.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "client-pitch-proposal",
    title: "Client Proposal Pitch Deck",
    description: "Customizable agency pitch deck outlining project deliverables, team allocation, pricing, and onboarding steps.",
    category: "Sales",
    time: "1 hour",
    downloads: 27100,
    likes: 2010,
    views: 56400,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Agency", "Consulting"],
    stage: ["All Stages"],
    creator: "STUDLYF Agency Hub",
    tags: ["Client Pitch", "Proposal", "Agency"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("client-pitch-proposal", "Client Proposal Pitch Deck", "Sales", "/templates/previews/user-template-corporate.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "crm-pipeline-presentation",
    title: "CRM & Sales Pipeline Review",
    description: "Sales pipeline tracking presentation covering deal stages, win rates, quota attainment, and revenue velocity.",
    category: "Sales",
    time: "1 hour",
    downloads: 15600,
    likes: 1190,
    views: 33400,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["Corporate", "Sales"],
    stage: ["All Stages"],
    creator: "CRM Ops Group",
    tags: ["CRM", "Pipeline", "Sales Management"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("crm-pipeline-presentation", "CRM & Sales Pipeline Review", "Sales", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/studlyf-master-deck.png")
  },
  {
    id: "quarterly-sales-report",
    title: "Quarterly Sales Performance Report",
    description: "Executive report presentation evaluating quarterly sales quotas, top deals closed, and churn impact.",
    category: "Sales",
    time: "1 hour",
    downloads: 20100,
    likes: 1510,
    views: 43200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Sales", "Enterprise"],
    stage: ["All Stages"],
    creator: "STUDLYF Sales Academy",
    tags: ["Sales Report", "Quotas", "Performance"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide2.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("quarterly-sales-report", "Quarterly Sales Performance Report", "Sales", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck.png")
  },

  // ─── 🎓 EDUCATION CATEGORY (6 TEMPLATES) ───
  {
    id: "final-year-project-deck",
    title: "Final Year Capstone Project Deck",
    description: "Academic presentation deck structured for engineering, computer science, and MBA capstone project defenses.",
    category: "Education",
    time: "1.5 hours",
    downloads: 29800,
    likes: 2280,
    views: 62400,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Education", "Academic"],
    stage: ["All Stages"],
    creator: "STUDLYF Academic Hub",
    tags: ["Capstone", "University", "Thesis", "Project"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("final-year-project-deck", "Final Year Capstone Project Deck", "Education", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck-slide4.png")
  },
  {
    id: "academic-seminar-deck",
    title: "University Research Seminar Deck",
    description: "Clean presentation deck for academic research seminars, paper walkthroughs, and conference talks.",
    category: "Education",
    time: "1 hour",
    downloads: 18200,
    likes: 1390,
    views: 39100,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Education", "Research"],
    stage: ["All Stages"],
    creator: "STUDLYF Academic Hub",
    tags: ["Seminar", "Research", "Academic"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide2.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("academic-seminar-deck", "University Research Seminar Deck", "Education", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck-slide4.png")
  },
  {
    id: "research-paper-presentation",
    title: "Scientific Research Paper Presentation",
    description: "Structured deck for presenting hypothesis, experimental methodology, data charts, and peer conclusions.",
    category: "Education",
    time: "1.5 hours",
    downloads: 16900,
    likes: 1280,
    views: 36400,
    rating: 4.8,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Research", "STEM", "Healthcare"],
    stage: ["All Stages"],
    creator: "Research Publishing Group",
    tags: ["Research Paper", "Science", "Methodology"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/studlyf-master-deck-slide3.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("research-paper-presentation", "Scientific Research Paper Presentation", "Education", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide4.png")
  },
  {
    id: "business-case-study",
    title: "MBA Business Case Study Deck",
    description: "Problem-solving case study framework analyzing industry challenges, strategic options, and financial ROI.",
    category: "Education",
    time: "1.5 hours",
    downloads: 24100,
    likes: 1820,
    views: 51200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Business", "Education"],
    stage: ["All Stages"],
    creator: "STUDLYF Academic Hub",
    tags: ["Case Study", "MBA", "Strategy"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("business-case-study", "MBA Business Case Study Deck", "Education", "/templates/previews/user-template-corporate.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png")
  },
  {
    id: "interactive-workshop-deck",
    title: "Interactive Training Workshop",
    description: "Engaging workshop presentation deck with breakout activity prompts, discussion slides, and key takeaways.",
    category: "Education",
    time: "1 hour",
    downloads: 21400,
    likes: 1610,
    views: 45900,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Education", "Corporate Training"],
    stage: ["All Stages"],
    creator: "STUDLYF Learning",
    tags: ["Workshop", "Training", "Education"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide2.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("interactive-workshop-deck", "Interactive Training Workshop", "Education", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png")
  },
  {
    id: "academic-thesis-deck",
    title: "Academic Doctoral Thesis Defense",
    description: "Formal presentation blueprint for PhD and Master thesis defense examinations and faculty reviews.",
    category: "Education",
    time: "2 hours",
    downloads: 14200,
    likes: 1090,
    views: 31200,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Education", "Academic"],
    stage: ["All Stages"],
    creator: "PhD Defense Guild",
    tags: ["Thesis", "PhD", "Defense"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/studlyf-master-deck-slide4.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("academic-thesis-deck", "Academic Doctoral Thesis Defense", "Education", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/studlyf-master-deck.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png")
  },

  // ─── 💻 TECHNOLOGY CATEGORY (6 TEMPLATES) ───
  {
    id: "ai-product-deck",
    title: "AI & LLM Product Presentation",
    description: "Cutting-edge presentation deck tailored for AI startups, agentic workflows, model evaluation, and LLM architecture.",
    category: "Technology",
    time: "1.5 hours",
    downloads: 39400,
    likes: 3120,
    views: 84200,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["AI", "Tech", "SaaS"],
    stage: ["Seed", "Series A"],
    creator: "AI Engineering Collective",
    tags: ["AI", "LLM", "Tech Product", "Agentic"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("ai-product-deck", "AI & LLM Product Presentation", "Technology", "/templates/previews/user-template-social.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "software-architecture-spec",
    title: "Software Architecture Blueprint",
    description: "Technical specification deck mapping cloud architecture, microservices topology, data flow, and security layers.",
    category: "Technology",
    time: "2 hours",
    downloads: 27800,
    likes: 2150,
    views: 58900,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Free",
    industry: ["Tech", "Software", "Cloud"],
    stage: ["All Stages"],
    creator: "STUDLYF Tech Lab",
    tags: ["Architecture", "System Design", "Microservices"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide1.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("software-architecture-spec", "Software Architecture Blueprint", "Technology", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-social.png", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "api-documentation-deck",
    title: "Developer API & SDK Platform Deck",
    description: "Developer-focused presentation detailing REST/GraphQL API specifications, rate limits, authentication, and SDKs.",
    category: "Technology",
    time: "1 hour",
    downloads: 19400,
    likes: 1480,
    views: 41200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Developer Tools", "Tech"],
    stage: ["All Stages"],
    creator: "STUDLYF Tech Lab",
    tags: ["API", "SDK", "Documentation", "DevTools"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("api-documentation-deck", "Developer API & SDK Platform Deck", "Technology", "/templates/previews/user-template-social.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "technical-proposal-blueprint",
    title: "Technical RFQ Proposal Deck",
    description: "Comprehensive engineering proposal outlining cloud migration, technology stack selection, and SLA guarantees.",
    category: "Technology",
    time: "2 hours",
    downloads: 16800,
    likes: 1290,
    views: 36400,
    rating: 4.7,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["IT Services", "Consulting"],
    stage: ["All Stages"],
    creator: "Enterprise Tech Practice",
    tags: ["Technical Proposal", "Engineering", "RFQ"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("technical-proposal-blueprint", "Technical RFQ Proposal Deck", "Technology", "/templates/previews/user-template-corporate.png", "/templates/previews/user-template-social.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "product-requirements-document",
    title: "Product Requirements Document (PRD)",
    description: "Structured PRD presentation mapping user stories, functional requirements, technical dependencies, and acceptance criteria.",
    category: "Technology",
    time: "1.5 hours",
    downloads: 25100,
    likes: 1920,
    views: 53200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Tech", "SaaS"],
    stage: ["All Stages"],
    creator: "STUDLYF Product Lab",
    tags: ["PRD", "Requirements", "Product"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-requirements-document", "Product Requirements Document (PRD)", "Technology", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/user-template-social.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "system-design-blueprint",
    title: "High-Scale System Design Deck",
    description: "System design architecture deck for presenting distributed databases, caching strategies, and fault tolerance.",
    category: "Technology",
    time: "2 hours",
    downloads: 22400,
    likes: 1740,
    views: 47800,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Tech", "Infrastructure"],
    stage: ["Growth", "Enterprise"],
    creator: "Cloud Architecture Guild",
    tags: ["System Design", "Cloud", "Scalability"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("system-design-blueprint", "High-Scale System Design Deck", "Technology", "/templates/previews/user-template-social.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-marketing.png", "/templates/previews/user-template-corporate.png")
  },

  // ─── 📱 PRODUCT CATEGORY (5 TEMPLATES) ───
  {
    id: "product-roadmap-deck",
    title: "Product Strategy & Roadmap Deck",
    description: "Visual product roadmap presentation mapping Now/Next/Later horizons, feature releases, and KPI impact.",
    category: "Product",
    time: "1.5 hours",
    downloads: 32100,
    likes: 2420,
    views: 67800,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["SaaS", "Mobile", "Tech"],
    stage: ["All Stages"],
    creator: "STUDLYF Product Lab",
    tags: ["Roadmap", "Product Strategy", "Features"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-roadmap-deck", "Product Strategy & Roadmap Deck", "Product", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "product-launch-deck",
    title: "Feature Release & Launch Deck",
    description: "Launch deck for presenting new product releases to internal teams, beta customers, and press.",
    category: "Product",
    time: "1 hour",
    downloads: 21800,
    likes: 1670,
    views: 45900,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["SaaS", "Consumer"],
    stage: ["All Stages"],
    creator: "STUDLYF Product Lab",
    tags: ["Product Launch", "Feature", "Release"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide4.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-launch-deck", "Feature Release & Launch Deck", "Product", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "feature-presentation-deck",
    title: "Key Feature Breakdown Deck",
    description: "Focused product presentation highlighting core user value, workflow comparison, and onboarding steps.",
    category: "Product",
    time: "45 mins",
    downloads: 17400,
    likes: 1310,
    views: 37200,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["SaaS", "App"],
    stage: ["All Stages"],
    creator: "STUDLYF Product Lab",
    tags: ["Feature", "Product Demo", "UX"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide3.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("feature-presentation-deck", "Key Feature Breakdown Deck", "Product", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "ux-case-study-deck",
    title: "UX/UI Case Study Presentation",
    description: "Comprehensive design case study deck showcasing problem research, wireframing, user testing, and final UI components.",
    category: "Product",
    time: "1.5 hours",
    downloads: 28400,
    likes: 2190,
    views: 59400,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["Design", "UX/UI", "Product"],
    stage: ["All Stages"],
    creator: "Design System Collective",
    tags: ["UX", "UI", "Case Study", "Design"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("ux-case-study-deck", "UX/UI Case Study Presentation", "Product", "/templates/previews/user-template-marketing.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/marketing-plan-slide3.png")
  },
  {
    id: "product-strategy-framework",
    title: "Product Growth Strategy Framework",
    description: "Strategic framework for optimizing product-led growth (PLG), user retention funnels, and feature monetization.",
    category: "Product",
    time: "1.5 hours",
    downloads: 23900,
    likes: 1810,
    views: 51200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["SaaS", "PLG"],
    stage: ["Seed", "Series A"],
    creator: "PLG Growth Guild",
    tags: ["Product Strategy", "PLG", "Retention"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("product-strategy-framework", "Product Growth Strategy Framework", "Product", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/marketing-plan-slide3.png", "/templates/previews/user-template-marketing.png")
  },

  // ─── 🏥 HEALTHCARE CATEGORY (4 TEMPLATES) ───
  {
    id: "hospital-management-deck",
    title: "Hospital Operations & Strategy Deck",
    description: "Executive presentation deck for healthcare administration, facility scaling, and patient outcomes metrics.",
    category: "Healthcare",
    time: "1.5 hours",
    downloads: 14200,
    likes: 1090,
    views: 31200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Healthcare", "Hospital"],
    stage: ["All Stages"],
    creator: "Healthcare Strategy Group",
    tags: ["Hospital", "Healthcare", "Operations"],
    isPopular: false,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/legal-founders-agreement.png",
    galleryImages: [
      "/templates/previews/legal-founders-agreement.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("hospital-management-deck", "Hospital Operations & Strategy Deck", "Healthcare", "/templates/previews/legal-founders-agreement.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "medical-startup-pitch",
    title: "HealthTech & Medical Startup Pitch",
    description: "Medical startup pitch deck detailing clinical validation, FDA/CE regulatory roadmap, and health system distribution.",
    category: "Healthcare",
    time: "2 hours",
    downloads: 19800,
    likes: 1540,
    views: 42100,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["HealthTech", "Biotech", "Medical Device"],
    stage: ["Seed", "Series A"],
    creator: "HealthTech Founders Network",
    tags: ["Medical", "HealthTech", "Pitch Deck", "FDA"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide1.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("medical-startup-pitch", "HealthTech & Medical Startup Pitch", "Healthcare", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/legal-founders-agreement.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "clinical-trial-report",
    title: "Clinical Trial Results Presentation",
    description: "Structured medical deck for presenting clinical trial methodologies, efficacy data, and patient safety outcomes.",
    category: "Healthcare",
    time: "2 hours",
    downloads: 11200,
    likes: 890,
    views: 24800,
    rating: 4.8,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["Pharma", "Biotech", "Clinical"],
    stage: ["All Stages"],
    creator: "Clinical Research Network",
    tags: ["Clinical Trial", "Data", "Medical"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: true,
    previewImage: "/templates/previews/studlyf-master-deck-slide3.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("clinical-trial-report", "Clinical Trial Results Presentation", "Healthcare", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/legal-founders-agreement.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/user-template-corporate.png")
  },
  {
    id: "healthcare-strategy-deck",
    title: "Digital Health Strategy Blueprint",
    description: "Strategic plan presentation for telemedicine expansion, HIPAA compliance, and digital patient engagement.",
    category: "Healthcare",
    time: "1.5 hours",
    downloads: 16500,
    likes: 1280,
    views: 36400,
    rating: 4.7,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Digital Health", "Telemedicine"],
    stage: ["All Stages"],
    creator: "Healthcare Strategy Group",
    tags: ["Digital Health", "Strategy", "Telemedicine"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-corporate.png",
    galleryImages: [
      "/templates/previews/user-template-corporate.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("healthcare-strategy-deck", "Digital Health Strategy Blueprint", "Healthcare", "/templates/previews/user-template-corporate.png", "/templates/previews/legal-founders-agreement.png", "/templates/previews/pitch-deck-slide1.png", "/templates/previews/studlyf-master-deck-slide3.png")
  },

  // ─── 🌱 SUSTAINABILITY CATEGORY (4 TEMPLATES) ───
  {
    id: "esg-impact-report",
    title: "Corporate ESG & Impact Report",
    description: "Comprehensive ESG presentation deck covering carbon footprint metrics, social initiatives, and governance standards.",
    category: "Sustainability",
    time: "2 hours",
    downloads: 21400,
    likes: 1650,
    views: 46200,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Sustainability", "Corporate"],
    stage: ["All Stages"],
    creator: "ESG Advisory Collective",
    tags: ["ESG", "Sustainability", "Impact"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide3.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide3.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("esg-impact-report", "Corporate ESG & Impact Report", "Sustainability", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "climatetech-pitch-deck",
    title: "ClimateTech & Clean Energy Pitch",
    description: "Investor presentation deck for renewable energy, carbon capture, and circular economy startups.",
    category: "Sustainability",
    time: "2 hours",
    downloads: 18900,
    likes: 1470,
    views: 41200,
    rating: 4.9,
    difficulty: "Advanced",
    price: "Premium",
    industry: ["ClimateTech", "CleanTech", "Energy"],
    stage: ["Pre-Seed", "Seed", "Series A"],
    creator: "Climate Investors Syndicate",
    tags: ["ClimateTech", "Clean Energy", "Pitch Deck"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/pitch-deck-slide2.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("climatetech-pitch-deck", "ClimateTech & Clean Energy Pitch", "Sustainability", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "sustainability-plan-blueprint",
    title: "Net Zero Sustainability Plan",
    description: "Actionable corporate roadmap for achieving net zero emissions, waste reduction, and sustainable supply chains.",
    category: "Sustainability",
    time: "1.5 hours",
    downloads: 15400,
    likes: 1190,
    views: 33400,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Corporate", "Supply Chain"],
    stage: ["All Stages"],
    creator: "Green Ops Lab",
    tags: ["Net Zero", "Sustainability", "Supply Chain"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide1.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide1.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("sustainability-plan-blueprint", "Net Zero Sustainability Plan", "Sustainability", "/templates/previews/marketing-plan-slide1.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/user-template-marketing.png")
  },
  {
    id: "green-startup-pitch",
    title: "Green & Circular Economy Pitch",
    description: "Pitch deck designed for eco-friendly product brands, sustainable packaging, and zero-waste initiatives.",
    category: "Sustainability",
    time: "1.5 hours",
    downloads: 16800,
    likes: 1310,
    views: 37200,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Eco", "D2C", "Consumer"],
    stage: ["Idea", "MVP", "Seed"],
    creator: "Green Ops Lab",
    tags: ["Green Startup", "Eco", "Circular Economy"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("green-startup-pitch", "Green & Circular Economy Pitch", "Sustainability", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/pitch-deck-slide2.png", "/templates/previews/marketing-plan-slide1.png")
  },

  // ─── 👨‍💼 HR CATEGORY (4 TEMPLATES) ───
  {
    id: "recruitment-plan-deck",
    title: "Talent Acquisition & Hiring Plan",
    description: "HR presentation deck mapping headcount planning, candidate sourcing pipelines, and employer branding.",
    category: "HR",
    time: "1.5 hours",
    downloads: 23100,
    likes: 1740,
    views: 51200,
    rating: 4.8,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["HR", "Corporate"],
    stage: ["All Stages"],
    creator: "STUDLYF People Ops",
    tags: ["Recruitment", "HR", "Hiring"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/linkedin-thought-leadership.png",
    galleryImages: [
      "/templates/previews/linkedin-thought-leadership.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("recruitment-plan-deck", "Talent Acquisition & Hiring Plan", "HR", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/user-template-social.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/marketing-plan-slide2.png")
  },
  {
    id: "company-culture-handbook",
    title: "Company Culture & Values Deck",
    description: "Inspiring company values deck detailing team philosophy, remote work guidelines, and employee perks.",
    category: "HR",
    time: "1 hour",
    downloads: 28900,
    likes: 2190,
    views: 61200,
    rating: 4.9,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Corporate", "Startups"],
    stage: ["All Stages"],
    creator: "STUDLYF People Ops",
    tags: ["Culture", "Values", "HR"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/user-template-social.png",
    galleryImages: [
      "/templates/previews/user-template-social.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("company-culture-handbook", "Company Culture & Values Deck", "HR", "/templates/previews/user-template-social.png", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/marketing-plan-slide2.png")
  },
  {
    id: "employee-handbook-deck",
    title: "New Employee Onboarding Handbook",
    description: "Comprehensive onboarding presentation guiding new hires through company policies, tech setup, and team contacts.",
    category: "HR",
    time: "1.5 hours",
    downloads: 26400,
    likes: 1980,
    views: 56400,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Premium",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "People & Culture Guild",
    tags: ["Onboarding", "Handbook", "HR"],
    isPopular: false,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/studlyf-master-deck-slide2.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("employee-handbook-deck", "New Employee Onboarding Handbook", "HR", "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/user-template-social.png", "/templates/previews/marketing-plan-slide2.png")
  },
  {
    id: "training-presentation-deck",
    title: "Internal Staff Training Deck",
    description: "Structured employee training presentation covering compliance rules, new software adoption, and workflow SOPs.",
    category: "HR",
    time: "1 hour",
    downloads: 19200,
    likes: 1450,
    views: 41200,
    rating: 4.7,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF People Ops",
    tags: ["Training", "SOP", "Staff"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/marketing-plan-slide2.png",
    galleryImages: [
      "/templates/previews/marketing-plan-slide2.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("training-presentation-deck", "Internal Staff Training Deck", "HR", "/templates/previews/marketing-plan-slide2.png", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/user-template-social.png", "/templates/previews/studlyf-master-deck-slide2.png")
  },

  // ─── 🎨 PORTFOLIO CATEGORY (4 TEMPLATES) ───
  {
    id: "personal-portfolio-deck",
    title: "Personal Executive Portfolio",
    description: "High-impact portfolio presentation deck for founders, executives, and consultants to showcase achievements.",
    category: "Portfolio",
    time: "1 hour",
    downloads: 31200,
    likes: 2410,
    views: 68400,
    rating: 4.9,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Executive", "Consulting"],
    stage: ["All Stages"],
    creator: "STUDLYF Design Studio",
    tags: ["Portfolio", "Executive", "Personal Brand"],
    isPopular: true,
    isNew: false,
    isFeatured: true,
    isPremium: false,
    previewImage: "/templates/previews/studlyf-master-deck-slide4.png",
    galleryImages: [
      "/templates/previews/studlyf-master-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("personal-portfolio-deck", "Personal Executive Portfolio", "Portfolio", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/user-template-marketing.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/linkedin-thought-leadership.png")
  },
  {
    id: "designer-portfolio-deck",
    title: "UI/UX & Creative Designer Portfolio",
    description: "Visually stunning portfolio presentation deck for UX/UI designers, art directors, and creative agencies.",
    category: "Portfolio",
    time: "1.5 hours",
    downloads: 34900,
    likes: 2780,
    views: 74200,
    rating: 4.9,
    difficulty: "Intermediate",
    price: "Premium",
    industry: ["Design", "Creative"],
    stage: ["All Stages"],
    creator: "Creative Design Guild",
    tags: ["Designer", "Portfolio", "UI/UX", "Creative"],
    isPopular: true,
    isNew: true,
    isFeatured: true,
    isPremium: true,
    previewImage: "/templates/previews/user-template-marketing.png",
    galleryImages: [
      "/templates/previews/user-template-marketing.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("designer-portfolio-deck", "UI/UX & Creative Designer Portfolio", "Portfolio", "/templates/previews/user-template-marketing.png", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/linkedin-thought-leadership.png")
  },
  {
    id: "photography-portfolio-deck",
    title: "Visual & Photography Showcase",
    description: "Minimalist visual portfolio deck tailored for photographers, videographers, and visual content creators.",
    category: "Portfolio",
    time: "1 hour",
    downloads: 18400,
    likes: 1420,
    views: 41200,
    rating: 4.8,
    difficulty: "Beginner",
    price: "Free",
    industry: ["Media", "Photography"],
    stage: ["All Stages"],
    creator: "STUDLYF Design Studio",
    tags: ["Photography", "Visual", "Showcase"],
    isPopular: false,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/pitch-deck-slide4.png",
    galleryImages: [
      "/templates/previews/pitch-deck-slide4.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("photography-portfolio-deck", "Visual & Photography Showcase", "Portfolio", "/templates/previews/pitch-deck-slide4.png", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/user-template-marketing.png", "/templates/previews/linkedin-thought-leadership.png")
  },
  {
    id: "resume-presentation-deck",
    title: "Interactive Resume & CV Presentation",
    description: "Modern presentation-style resume deck designed to stand out in high-level executive and tech job interviews.",
    category: "Portfolio",
    time: "1 hour",
    downloads: 29400,
    likes: 2210,
    views: 62400,
    rating: 4.9,
    difficulty: "Beginner",
    price: "Free",
    industry: ["All Industries"],
    stage: ["All Stages"],
    creator: "STUDLYF Design Studio",
    tags: ["Resume", "CV", "Career", "Interview"],
    isPopular: true,
    isNew: false,
    isFeatured: false,
    isPremium: false,
    previewImage: "/templates/previews/linkedin-thought-leadership.png",
    galleryImages: [
      "/templates/previews/linkedin-thought-leadership.png"
    ],
    structureType: "slides",
    slides: generateUniqueSlides("resume-presentation-deck", "Interactive Resume & CV Presentation", "Portfolio", "/templates/previews/linkedin-thought-leadership.png", "/templates/previews/studlyf-master-deck-slide4.png", "/templates/previews/user-template-marketing.png", "/templates/previews/pitch-deck-slide4.png")
  }
];

export const getDynamicTemplates = () => {
  return TEMPLATES_DATA;
};

export const addUploadedTemplate = (newTmpl) => {
  if (!newTmpl || !newTmpl.title) return null;
  const id = `custom-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;
  const created = {
    id,
    title: newTmpl.title,
    description: newTmpl.description || "Uploaded professional template design.",
    category: newTmpl.category || "Pitch Decks",
    time: "1 hour",
    downloads: 1,
    likes: 1,
    views: 10,
    rating: 5.0,
    difficulty: "Intermediate",
    price: "Free",
    industry: ["Custom"],
    stage: ["All Stages"],
    creator: "You (Custom Upload)",
    tags: ["Custom", newTmpl.category || "Template"],
    isPopular: false,
    isNew: true,
    isFeatured: false,
    isPremium: false,
    isCustom: true,
    previewImage: newTmpl.previewImage || "/templates/previews/studlyf-master-deck.png",
    galleryImages: newTmpl.galleryImages || [newTmpl.previewImage],
    structureType: "slides",
    slides: generateUniqueSlides(id, newTmpl.title, newTmpl.category || "Custom", newTmpl.previewImage, "/templates/previews/studlyf-master-deck-slide2.png", "/templates/previews/studlyf-master-deck-slide3.png", "/templates/previews/studlyf-master-deck-slide4.png")
  };

  TEMPLATES_DATA.unshift(created);
  return created;
};
